import { motion, AnimatePresence } from 'framer-motion';
import { Key, MapPin, User, Phone, Lock, ParkingCircle } from 'lucide-react';
import { OrderFormData, AccessMode } from '@/types/order';
import { Input } from '@/components/ui/input';

interface Step6AccesProps {
  data: OrderFormData;
  onChange: (data: Partial<OrderFormData>) => void;
}

const modesAcces: { value: AccessMode; label: string; description: string }[] = [
  {
    value: 'cles_etude',
    label: 'Clés à l\'étude',
    description: 'Les clés sont disponibles à votre étude',
  },
  {
    value: 'code',
    label: 'Code d\'accès',
    description: 'Accès via code/digicode',
  },
  {
    value: 'cles_ailleurs',
    label: 'Clés ailleurs',
    description: 'Retrait des clés à une autre adresse',
  },
];

export function Step6Acces({ data, onChange }: Step6AccesProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div>
        <h3 className="text-3xl font-display font-bold text-navy-600 mb-2">
          Accès au bien
        </h3>
        <p className="text-lg text-navy-400">Comment accéder au bien pour les visites</p>
      </div>

      {/* Mode d'accès */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-3">
          <Key className="w-4 h-4 text-gold-600" />
          Mode d'accès *
        </label>
        <div className="grid md:grid-cols-3 gap-4">
          {modesAcces.map((mode) => {
            const isSelected = data.modeAcces === mode.value;
            return (
              <button
                key={mode.value}
                type="button"
                onClick={() => onChange({ modeAcces: mode.value })}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected
                    ? 'border-gold-500 bg-gold-50/50'
                    : 'border-navy-100 bg-white hover:border-navy-200'
                }`}
              >
                <p className={`font-semibold text-sm mb-1 ${isSelected ? 'text-navy-600' : 'text-navy-600'}`}>
                  {mode.label}
                </p>
                <p className="text-xs text-navy-400">{mode.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Clés à l'étude */}
      <AnimatePresence>
        {data.modeAcces === 'cles_etude' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-6 bg-cream rounded-2xl border border-navy-200"
          >
            <div className="flex items-start gap-3 mb-4">
              <Key className="w-5 h-5 text-gold-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-navy-600 mb-1">
                  Retrait des clés à votre étude
                </h4>
                <p className="text-sm text-navy-400">
                  Les clés seront retirées à l'adresse suivante :
                </p>
              </div>
            </div>

            <div className="pl-8 space-y-2 text-sm text-navy-600">
              <p className="font-medium">{data.societe}</p>
              <p>{data.adresseDonneurRue}</p>
              <p>{data.adresseDonneurCP} {data.adresseDonneurVille}</p>
              <p className="flex items-center gap-2 text-navy-400 mt-3">
                <Phone className="w-4 h-4" />
                {data.telephone}
              </p>
            </div>
          </motion.div>
        )}

        {/* Code d'accès */}
        {data.modeAcces === 'code' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            <div>
              <label htmlFor="codeAcces" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
                <Lock className="w-4 h-4 text-gold-600" />
                Code d'accès *
              </label>
              <Input
                id="codeAcces"
                type="text"
                required
                value={data.codeAcces}
                onChange={(e) => onChange({ codeAcces: e.target.value })}
                placeholder="Ex: 1234A, #5678, etc."
              />
              <p className="text-xs text-navy-400 mt-1">
                Code porte, digicode, code boîte à clés...
              </p>
            </div>
          </motion.div>
        )}

        {/* Clés ailleurs */}
        {data.modeAcces === 'cles_ailleurs' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4 p-6 bg-cream rounded-2xl border border-navy-200"
          >
            <h4 className="font-semibold text-navy-600 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-gold-600" />
              Lieu de retrait des clés
            </h4>

            <div>
              <label htmlFor="adresseRetraitCles" className="block text-sm font-medium text-navy-600 mb-2">
                Adresse de retrait *
              </label>
              <Input
                id="adresseRetraitCles"
                type="text"
                required
                value={data.adresseRetraitCles}
                onChange={(e) => onChange({ adresseRetraitCles: e.target.value })}
                placeholder="Adresse complète"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contactRetraitClesNom" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
                  <User className="w-4 h-4 text-gold-600" />
                  Nom du contact *
                </label>
                <Input
                  id="contactRetraitClesNom"
                  type="text"
                  required
                  value={data.contactRetraitClesNom}
                  onChange={(e) => onChange({ contactRetraitClesNom: e.target.value })}
                  placeholder="Nom du contact"
                />
              </div>

              <div>
                <label htmlFor="contactRetraitClesTelephone" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
                  <Phone className="w-4 h-4 text-gold-600" />
                  Téléphone *
                </label>
                <Input
                  id="contactRetraitClesTelephone"
                  type="tel"
                  required
                  value={data.contactRetraitClesTelephone}
                  onChange={(e) => onChange({ contactRetraitClesTelephone: e.target.value })}
                  placeholder="+32 XXX XX XX XX"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Parking et contraintes d'accès */}
      <div>
        <label htmlFor="parkingAccesContraintes" className="flex items-center gap-2 text-sm font-medium text-navy-600 mb-2">
          <ParkingCircle className="w-4 h-4 text-gold-600" />
          Parking & contraintes d'accès (optionnel)
        </label>
        <p className="text-sm text-navy-400 mb-3">
          Informations sur le stationnement, difficultés d'accès, particularités...
        </p>
        <textarea
          id="parkingAccesContraintes"
          rows={3}
          value={data.parkingAccesContraintes}
          onChange={(e) => onChange({ parkingAccesContraintes: e.target.value })}
          placeholder="Ex: Parking gratuit dans la rue, accès par l'arrière du bâtiment, ascenseur en panne..."
          className="w-full px-4 py-3 rounded-xl border border-navy-200 focus:border-gold-500 focus:outline-none focus:ring-4 focus:ring-gold-500/20 transition-all resize-none text-navy-600 placeholder:text-navy-300"
        />
      </div>
    </motion.div>
  );
}
